<template>
  <v-row>
    <v-col>
      <v-card class="elevation-4" rounded="lg">
        <v-card-title class="primary white--text">
          <v-icon left dark>mdi-chart-bar</v-icon>
          <span class="text-h6">Desempenho por Disciplina</span>
        </v-card-title>

        <v-card-text class="pa-4">
          <Bar
            :chart-options="chartOptions"
            :chart-data="chartData"
            :chart-id="chartId"
            :dataset-id-key="datasetIdKey"
            :plugins="plugins"
            :css-classes="cssClasses"
            :styles="styles"
            :width="width"
            :height="height"
          />
        </v-card-text>

        <v-card-actions class="pa-3 grey lighten-4">
          <v-btn color="primary" text depressed @click="show = !show">
            <v-icon left>mdi-information-outline</v-icon>
            Método de Avaliação
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn icon @click="show = !show" color="primary">
            <v-icon>{{ show ? "mdi-chevron-down" : "mdi-chevron-up" }}</v-icon>
          </v-btn>
        </v-card-actions>

        <v-expand-transition>
          <div v-show="!show">
            <v-divider></v-divider>

            <v-card-text class="info-box">
              <v-icon color="primary" class="mr-2">mdi-alert-circle-outline</v-icon>
              <span class="text-subtitle-2 primary--text">Como interpretar este gráfico:</span>

              <ul class="mt-2 pl-4">
                <li>
                  As médias, no gráfico, são calculadas de acordo com a quantidade de provas
                  realizadas.
                  <br /><span class="text-caption grey--text"
                    >Exemplo: se o(a) aluno(a) realizou apenas 2 provas, a média será calculada
                    entre essas.</span
                  >
                </li>
                <li class="mt-2">
                  As avaliações são calculadas de acordo com o resultado da mediana entre 0 e 10.
                  <br /><span class="text-caption grey--text"
                    >Exemplo: média 10 em língua portuguesa e 6 em matemática, a soma dividida por 2
                    gera a avaliação final.</span
                  >
                </li>
              </ul>
            </v-card-text>
          </div>
        </v-expand-transition>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js";

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

export default {
  name: "BarChart",
  components: { Bar },
  watch: {
    disciplinas(value) {
      this.chartData.labels = value;
    },
    notas(value) {
      this.chartData.datasets = [
        {
          ...value,
          backgroundColor: this.generateGradientColors(value.data.length),
          borderRadius: 4,
          borderSkipped: false,
        },
      ];
    },
  },
  props: {
    disciplinas: {
      type: Array,
      default: () => [],
    },
    notas: {
      type: Array,
      default: () => [],
    },
    chartId: {
      type: String,
      default: "bar-chart",
    },
    datasetIdKey: {
      type: String,
      default: "label",
    },
    width: {
      type: Number,
      default: 50,
    },
    height: {
      type: Number,
      default: 50,
    },
    cssClasses: {
      default: "",
      type: String,
    },
    styles: {
      type: Object,
      default: () => {},
    },
    plugins: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      show: true,
      chartData: {
        labels: this.disciplinas,
        datasets: [
          {
            ...this.notas,
            backgroundColor: this.generateGradientColors(this.notas.data?.length || 0),
            borderRadius: 4,
            borderSkipped: false,
          },
        ],
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: "top",
            labels: {
              font: {
                size: 12,
                family: "Roboto",
              },
              padding: 20,
              usePointStyle: true,
              pointStyle: "circle",
            },
          },
          tooltip: {
            backgroundColor: "#424242",
            titleFont: {
              size: 14,
              family: "Roboto",
              weight: "bold",
            },
            bodyFont: {
              size: 12,
              family: "Roboto",
            },
            padding: 12,
            cornerRadius: 4,
            displayColors: false,
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 10,
            ticks: {
              stepSize: 1,
              font: {
                family: "Roboto",
              },
            },
            grid: {
              drawBorder: false,
              color: "rgba(0,0,0,0.05)",
            },
          },
          x: {
            grid: {
              display: false,
              drawBorder: false,
            },
            ticks: {
              font: {
                family: "Roboto",
              },
            },
          },
        },
      },
    };
  },
  methods: {
    generateGradientColors(count) {
      const colors = [];
      for (let i = 0; i < count; i++) {
        const hue = 40 + i * 30;
        colors.push(`hsla(${hue}, 90%, 60%, 0.8)`);
      }
      return colors;
    },
  },
};
</script>

<style scoped>
.info-box {
  background-color: #f5f5f5;
  border-left: 4px solid var(--v-primary-base);
}

.v-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.v-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
}

.info-box::-webkit-scrollbar {
  width: 6px;
}

.info-box::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.info-box::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.info-box::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
